import { useCallback, useMemo, useState } from "react";
import { NativeInvoiceDraftBody } from "@common/schemas/domains/invoice/native-invoice.schema";
import { InvoiceBase, InvoiceStatus } from "@common/types/invoice";
import { NativeInvoiceDetailBase } from "@common/types/nativeInvoice";
import {
    useCreateNativeInvoice,
    useUpdateNativeInvoice,
} from "@api/native-invoice.api";
import type { NativeInvoiceLineCodingByLineId } from "./nativeInvoiceFormTypes";
import { toNativeInvoiceUpdateBody } from "./buildNativeInvoiceDraftBody";

interface Params {
    workspaceId: string | undefined;
    // The native invoice API is scoped per business entity (it lives at
    // /invoices/:workspaceId/:entityId/native), so saving requires the
    // currently selected entity. Saves are skipped until one is available.
    entityId: number | undefined;
    initialInvoiceId?: string;
    draft: NativeInvoiceDraftBody | null;
    // Coding snapshot paired with `draft` from the same render. Captured at
    // save-start and passed to onSaveSuccess so the form can advance its
    // baseline from what was persisted, not from later in-flight edits.
    draftLineCodingBaseline?: NativeInvoiceLineCodingByLineId;
    // Raw-state fingerprint used for the close-confirmation guard (distinct
    // from the save-dedup hash below). Captured at save-start; a successful
    // save advances the dirty baseline to it, so edits made mid-save stay
    // dirty. See buildNativeInvoiceDirtyFingerprint.
    dirtyFingerprint: string;
    billableExpenseTransactionIds?: number[];
    sourceTemplateId?: string;
    onSaveSuccess?: (
        result: {
            data: InvoiceBase;
            nativeDetail: NativeInvoiceDetailBase;
        },
        meta: { savedLineCodingBaseline: NativeInvoiceLineCodingByLineId },
    ) => void;
}

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export interface SaveResult {
    invoiceId: string | undefined;
    status: SaveStatus;
    lastSavedAt: Date | null;
    // True when the raw form fingerprint has diverged from the last-saved
    // baseline. An untouched create is clean; a successful save clears it (for
    // the fingerprint captured when the save started).
    isDirty: boolean;
    // Announces that the create draft is about to be rescoped to `nextEntityId`.
    // Must be called from the From-entity handler, before the switch's own state
    // updates land: at that point `dirtyFingerprint` still holds the pre-switch
    // value, so a draft with no unsaved work can still be recognized. Afterwards
    // the entity-derived rewrite (From block, logo, suggested number) is
    // indistinguishable from a user edit.
    acknowledgeEntityRescope: (nextEntityId: number | undefined) => void;
    // Folds an entity-derived From address that resolved *after* a clean
    // rescope into the clean baseline. The rescope re-bases the baseline before
    // the async address lands (see `acknowledgeEntityRescope`), so the later
    // address write would otherwise read as unsaved user work and prompt on
    // close. Call it only when the form was clean immediately before applying
    // the address; a real edit made during the fetch must stay dirty.
    rebaseCleanBaselineAfterAddress: (entityId: number) => void;
    // Folds a just-applied template into the baseline, so picking a template
    // (including the default applied on open) is not unsaved user work and
    // closing an otherwise untouched draft doesn't prompt. Only meaningful
    // before anything is persisted: once a draft exists on the server, swapping
    // its template IS a change and must keep prompting.
    rebaseBaselineAfterTemplateApply: () => void;
    save: (options?: { status?: InvoiceStatus }) => Promise<string | undefined>;
}

// All save bookkeeping lives in one state object (no refs): the id created so
// far, the last-saved hash, the save indicator, the entity-switch generation,
// and the in-flight save promise used to dedupe concurrent saves.
interface SaveState {
    invoiceId: string | undefined;
    status: SaveStatus;
    lastSavedAt: Date | null;
    lastSavedHash: string;
    // The raw fingerprint as of the last successful save (or initial mount).
    // isDirty compares the live fingerprint against this.
    baselineFingerprint: string;
    // Bumped whenever the draft is rescoped to a different entity. A save
    // captures the generation when it starts and only commits its result if the
    // generation still matches, so an in-flight create from a previous entity
    // can't repopulate the invoice id and trigger a cross-entity PATCH.
    generation: number;
    // Entity the draft is being rescoped to from a clean form, recorded by
    // `acknowledgeEntityRescope`. The rescope reset re-bases the dirty baseline
    // only when the entity actually becomes this one, so a stale record cannot
    // silently clear the dirty state of a later, unrelated entity change.
    rebaseBaselineForEntityId: number | null;
    // Entity whose async From address is pending a fold into the clean baseline,
    // recorded by `rebaseCleanBaselineAfterAddress`. Consumed on the render
    // after the address write lands, when `dirtyFingerprint` reflects it, so the
    // baseline advances onto the address-inclusive fingerprint. A later entity
    // switch drops it so a stale address can't clear an unrelated draft.
    foldAddressBaselineForEntityId: number | null;
    // Set by `rebaseBaselineAfterTemplateApply` while a template write is
    // landing. Consumed on the render where `dirtyFingerprint` reflects the
    // template, which then becomes the clean baseline.
    isTemplateBaselineFoldPending: boolean;
    // The current save's promise; a concurrent `save` reuses it instead of
    // firing a second request, unless the new call needs OPEN and the
    // in-flight one does not.
    inFlight: Promise<string | undefined> | null;
    inFlightStatus?: InvoiceStatus.OPEN;
}

function hashDraft(draft: NativeInvoiceDraftBody | null): string {
    if (!draft) {
        return "";
    }
    return JSON.stringify(draft);
}

/**
 * Persists the current draft to the server on demand: creates a new native
 * invoice on first save, then patches it on subsequent saves. Skips writes
 * when the serialized draft hasn't changed since the last save, so repeated
 * saves of an unchanged draft don't trigger redundant requests.
 */
export function useNativeInvoiceSave({
    workspaceId,
    entityId,
    initialInvoiceId,
    draft,
    draftLineCodingBaseline,
    dirtyFingerprint,
    billableExpenseTransactionIds,
    sourceTemplateId,
    onSaveSuccess,
}: Params): SaveResult {
    const createMutation = useCreateNativeInvoice();
    const updateMutation = useUpdateNativeInvoice();

    const [saveState, setSaveState] = useState<SaveState>(() => ({
        invoiceId: initialInvoiceId,
        status: "idle",
        lastSavedAt: null,
        lastSavedHash: "",
        // Baseline starts at the initial fingerprint so an untouched form is
        // clean out of the gate.
        baselineFingerprint: dirtyFingerprint,
        generation: 0,
        rebaseBaselineForEntityId: null,
        foldAddressBaselineForEntityId: null,
        isTemplateBaselineFoldPending: false,
        inFlight: null,
    }));

    // Read (and optionally commit) the latest save state from inside a React
    // state updater — the only ref-free way for an async save to see state that
    // changed after it started (e.g. a generation bump from an entity switch)
    // and to atomically claim the in-flight slot against concurrent saves.
    // The updater must stay deterministic (React may invoke it twice under
    // StrictMode), so its side effects — resolving the promise, installing the
    // in-flight marker — are idempotent and safe under double-invocation.
    const commitSaveState = useCallback(
        (updater: (current: SaveState) => SaveState): Promise<SaveState> =>
            new Promise((resolve) => {
                setSaveState((current) => {
                    const next = updater(current);
                    resolve(next);
                    return next;
                });
            }),
        [],
    );

    // Records a clean rescope while `dirtyFingerprint` is still the pre-switch
    // value. A draft with unsaved work is left unrecorded, so it keeps its
    // baseline across the switch and still prompts on close.
    const acknowledgeEntityRescope = useCallback(
        (nextEntityId: number | undefined) => {
            setSaveState((current) =>
                nextEntityId !== undefined &&
                current.baselineFingerprint === dirtyFingerprint
                    ? { ...current, rebaseBaselineForEntityId: nextEntityId }
                    : current,
            );
        },
        [dirtyFingerprint],
    );

    const rebaseCleanBaselineAfterAddress = useCallback(
        (resolvedEntityId: number) => {
            setSaveState((current) => ({
                ...current,
                foldAddressBaselineForEntityId: resolvedEntityId,
            }));
        },
        [],
    );

    const rebaseBaselineAfterTemplateApply = useCallback(() => {
        setSaveState((current) =>
            current.invoiceId === undefined
                ? { ...current, isTemplateBaselineFoldPending: true }
                : current,
        );
    }, []);

    const draftHash = useMemo(() => hashDraft(draft), [draft]);

    // Two "sync local state from a changed prop" transitions, both handled with
    // the "adjust state during render" pattern instead of effects: comparing
    // against the last-seen prop value and updating the derived state in the
    // same render pass avoids an extra render + effect round-trip, and (for the
    // entity-switch reset) lets the invalidation take effect before this same
    // render's `save` closure captures a stale generation.

    // 1. initialInvoiceId can arrive/late-resolve after mount (e.g. edit-mode
    //    invoice id resolving from a slower query). Adopt it once seen.
    const [prevInitialInvoiceId, setPrevInitialInvoiceId] =
        useState(initialInvoiceId);
    if (
        initialInvoiceId !== prevInitialInvoiceId &&
        initialInvoiceId !== undefined
    ) {
        setPrevInitialInvoiceId(initialInvoiceId);
        if (saveState.invoiceId !== initialInvoiceId) {
            setSaveState((current) => ({
                ...current,
                invoiceId: initialInvoiceId,
            }));
        }
    }

    // 2. The draft is rescoped to a different entity when `entityId` changes
    //    (and this isn't the fixed edit-mode invoice). Invalidate any in-flight
    //    create from the previous entity, drop the cached id/hash, and reset
    //    the save indicator so it reflects the fresh draft instead of the
    //    previous entity's "Saved" state.
    const [prevEntityId, setPrevEntityId] = useState(entityId);
    if (entityId !== prevEntityId) {
        setPrevEntityId(entityId);
        if (initialInvoiceId === undefined) {
            setSaveState((current) => ({
                ...current,
                generation: current.generation + 1,
                inFlight: null,
                inFlightStatus: undefined,
                invoiceId: undefined,
                lastSavedHash: "",
                status: "idle",
                lastSavedAt: null,
                // A rescope the caller acknowledged as clean advances the
                // baseline onto the rescoped form: the switch rewrote only
                // entity-derived fields, which is not unsaved user work. A
                // rescope of a dirty draft keeps its baseline and stays dirty,
                // since the whole form is now unsaved (the id was just dropped).
                baselineFingerprint:
                    current.rebaseBaselineForEntityId === entityId
                        ? dirtyFingerprint
                        : current.baselineFingerprint,
                rebaseBaselineForEntityId: null,
            }));
        }
    }

    // 3. A clean-rescope From address resolved asynchronously and was written to
    //    the form. Fold it into the baseline on this render (dirtyFingerprint now
    //    reflects the address) so the entity-derived write does not read as
    //    unsaved work. The fold is consumed once; it advances the baseline only
    //    when the address still targets the current entity, so a later entity
    //    switch drops it instead of clearing an unrelated draft's dirty state.
    if (saveState.foldAddressBaselineForEntityId !== null) {
        const shouldFold =
            saveState.foldAddressBaselineForEntityId === entityId &&
            saveState.baselineFingerprint !== dirtyFingerprint;
        setSaveState((current) => ({
            ...current,
            baselineFingerprint: shouldFold
                ? dirtyFingerprint
                : current.baselineFingerprint,
            foldAddressBaselineForEntityId: null,
        }));
    }

    // 4. A template was applied to a draft that has nothing persisted yet. The
    //    write replaces the whole form with the template's content, which is a
    //    new starting point rather than unsaved user work, so it becomes the
    //    clean baseline. Consumed once, and only while the draft is still
    //    unsaved: re-templating a saved draft is a real change.
    if (saveState.isTemplateBaselineFoldPending) {
        const shouldFold =
            saveState.invoiceId === undefined &&
            saveState.baselineFingerprint !== dirtyFingerprint;
        setSaveState((current) => ({
            ...current,
            baselineFingerprint: shouldFold
                ? dirtyFingerprint
                : current.baselineFingerprint,
            isTemplateBaselineFoldPending: false,
        }));
    }

    // TanStack Query v5 `mutateAsync` references are stable across renders,
    // so we reference them directly to avoid recreating the callback every
    // render when the mutation object itself changes identity.
    const createAsync = createMutation.mutateAsync;
    const updateAsync = updateMutation.mutateAsync;

    const save = useCallback(
        async function saveDraft(options?: {
            status?: InvoiceStatus;
        }): Promise<string | undefined> {
            if (!workspaceId || entityId === undefined || !draft) {
                const { invoiceId } = await commitSaveState(
                    (current) => current,
                );
                return invoiceId;
            }

            // Pair with the closed-over `draft` so mid-flight coding edits are not
            // treated as persisted when onSaveSuccess advances the form baseline.
            const savedLineCodingBaseline = draftLineCodingBaseline ?? {};
            // Snapshot the dirty fingerprint at save-start; a successful save
            // advances the baseline to exactly this, so edits made while saving
            // remain dirty.
            const savedFingerprint = dirtyFingerprint;

            // Deferred promise created before the claim so it stays stable across
            // the updater's StrictMode double-invocation. It becomes the shared
            // in-flight promise that concurrent saves reuse.
            let resolveSave: (value: string | undefined) => void = () => {};
            let rejectSave: (reason?: unknown) => void = () => {};
            const inFlightPromise = new Promise<string | undefined>(
                (resolve, reject) => {
                    resolveSave = resolve;
                    rejectSave = reject;
                },
            );

            // Atomically claim the save slot inside a functional state update.
            // React applies queued updaters in order against the committed state,
            // so two save() calls in the same tick serialize here: the second
            // observes the first's in-flight promise and reuses it instead of
            // firing a duplicate request (the synchronous double-click dedupe the
            // previous inFlightRef provided). Reading invoiceId/lastSavedHash/
            // generation from the committed state (not the render closure) also
            // lets `save` drop the whole-`saveState` dependency and stay
            // identity-stable across save-status transitions.
            let claim:
                | { kind: "reuse"; promise: Promise<string | undefined> }
                | { kind: "followUp"; promise: Promise<string | undefined> }
                | { kind: "skip"; invoiceId: string | undefined }
                | {
                      kind: "run";
                      generation: number;
                      invoiceId: string | undefined;
                  }
                | undefined;
            await commitSaveState((current) => {
                if (current.inFlight) {
                    const incomingWantsOpen =
                        options?.status === InvoiceStatus.OPEN;
                    const inFlightIsOpen =
                        current.inFlightStatus === InvoiceStatus.OPEN;
                    claim =
                        incomingWantsOpen && !inFlightIsOpen
                            ? {
                                  kind: "followUp",
                                  promise: current.inFlight,
                              }
                            : { kind: "reuse", promise: current.inFlight };
                    return current;
                }
                if (
                    options?.status !== InvoiceStatus.OPEN &&
                    draftHash === current.lastSavedHash
                ) {
                    claim = { kind: "skip", invoiceId: current.invoiceId };
                    return current;
                }
                claim = {
                    kind: "run",
                    generation: current.generation,
                    invoiceId: current.invoiceId,
                };
                return {
                    ...current,
                    status: "saving",
                    inFlight: inFlightPromise,
                    inFlightStatus:
                        options?.status === InvoiceStatus.OPEN
                            ? InvoiceStatus.OPEN
                            : undefined,
                };
            });

            if (!claim) {
                return undefined;
            }
            if (claim.kind === "reuse") {
                return await claim.promise;
            }
            if (claim.kind === "followUp") {
                await claim.promise;
                return await saveDraft(options);
            }
            if (claim.kind === "skip") {
                return claim.invoiceId;
            }

            const { generation, invoiceId: currentId } = claim;

            const persist = async (): Promise<string | undefined> => {
                try {
                    if (currentId) {
                        const result = await updateAsync({
                            workspaceId,
                            entityId,
                            invoiceId: currentId,
                            body: {
                                ...toNativeInvoiceUpdateBody(draft),
                                ...(options?.status === InvoiceStatus.OPEN
                                    ? { status: InvoiceStatus.OPEN }
                                    : {}),
                            },
                        });
                        const savedAt = new Date();
                        const committed = await commitSaveState((current) =>
                            current.generation === generation
                                ? {
                                      ...current,
                                      lastSavedHash: draftHash,
                                      lastSavedAt: savedAt,
                                      baselineFingerprint: savedFingerprint,
                                      status: "saved",
                                  }
                                : current,
                        );
                        if (committed.generation !== generation) {
                            resolveSave(undefined);
                            return undefined;
                        }
                        if (onSaveSuccess) {
                            onSaveSuccess(result, { savedLineCodingBaseline });
                        }
                        resolveSave(currentId);
                        return currentId;
                    }
                    const result = await createAsync({
                        workspaceId,
                        entityId,
                        body: {
                            ...draft,
                            ...(options?.status === InvoiceStatus.OPEN
                                ? { status: InvoiceStatus.OPEN }
                                : {}),
                            ...(billableExpenseTransactionIds &&
                            billableExpenseTransactionIds.length > 0
                                ? { billableExpenseTransactionIds }
                                : {}),
                            ...(sourceTemplateId ? { sourceTemplateId } : {}),
                        },
                    });
                    // The entity changed while this create was in flight; the new id
                    // belongs to the previous entity, so don't adopt it — doing so
                    // would PATCH the old draft under the new entity's endpoint.
                    const newId = result.data.id;
                    const savedAt = new Date();
                    const committed = await commitSaveState((current) =>
                        current.generation === generation
                            ? {
                                  ...current,
                                  invoiceId: newId,
                                  lastSavedHash: draftHash,
                                  lastSavedAt: savedAt,
                                  baselineFingerprint: savedFingerprint,
                                  status: "saved",
                              }
                            : current,
                    );
                    if (committed.generation !== generation) {
                        resolveSave(undefined);
                        return undefined;
                    }
                    if (onSaveSuccess) {
                        onSaveSuccess(result, { savedLineCodingBaseline });
                    }
                    resolveSave(newId);
                    return newId;
                } catch (error) {
                    await commitSaveState((current) =>
                        current.generation === generation
                            ? { ...current, status: "error" }
                            : current,
                    );
                    // Reject the shared promise so reuse callers observe the failure,
                    // then rethrow via the awaited promise below to the direct caller.
                    rejectSave(error);
                }
                return await inFlightPromise;
            };
            // Only clear the in-flight promise if a newer generation hasn't
            // already installed a fresh save, so a stale resolution can't
            // wipe the current in-flight promise.
            return await persist().finally(() =>
                commitSaveState((current) =>
                    current.generation === generation
                        ? {
                              ...current,
                              inFlight: null,
                              inFlightStatus: undefined,
                          }
                        : current,
                ),
            );
        },
        [
            workspaceId,
            entityId,
            draft,
            draftHash,
            draftLineCodingBaseline,
            dirtyFingerprint,
            createAsync,
            updateAsync,
            onSaveSuccess,
            billableExpenseTransactionIds,
            sourceTemplateId,
            commitSaveState,
        ],
    );

    return {
        invoiceId: saveState.invoiceId,
        status: saveState.status,
        lastSavedAt: saveState.lastSavedAt,
        isDirty: saveState.baselineFingerprint !== dirtyFingerprint,
        acknowledgeEntityRescope,
        rebaseCleanBaselineAfterAddress,
        rebaseBaselineAfterTemplateApply,
        save,
    };
}
